import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { assessmentsAPI, comparisonAPI } from '../../services/api';

const MAX_COMPARISON_ITEMS = 6;

/**
 * Comparison admin hook — encapsulates:
 *  - Multi-assessment selection & drill-down state
 *  - AI comparison analysis flow
 *  - Radar (spider) data + aggregated stats derivation
 *  - Bulk delete from comparison
 *
 * Depends on a callback `onAssessmentsDeleted(ids)` so AdminDashboard can
 * update its `assessments` state without leaking that source-of-truth here.
 */
export const useComparisonAdmin = (onAssessmentsDeleted) => {
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  const [comparisonDrillDown, setComparisonDrillDown] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [comparisonAnalysis, setComparisonAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  // Ref mirror of selection: lets generateComparisonAnalysis be a STABLE callback
  // (empty deps) while still reading the freshest selection — works around the
  // AdminContext `adminActions` useMemo(() => ..., []) which freezes function
  // references at first render.
  const selectionRef = useRef([]);
  useEffect(() => {
    selectionRef.current = selectedForComparison;
  }, [selectedForComparison]);


  // Defensive auto-clear: any time the selection contains >= 2 items, the
  // hardcoded "Selecteer minimaal 2..." error must NEVER be visible.
  // This guards against race conditions where the error was set while length
  // was < 2 and a stale state lingered, or where the toggle handler was bypassed.
  useEffect(() => {
    if (selectedForComparison.length >= 2 && analysisError === 'Selecteer minimaal 2 quickscans om te vergelijken') {
      setAnalysisError(null);
    }
  }, [selectedForComparison.length, analysisError]);

  const toggleComparisonSelection = useCallback((assessment) => {
    setSelectedForComparison((prev) => {
      const isSelected = prev.some((a) => a.id === assessment.id);
      if (isSelected) {
        return prev.filter((a) => a.id !== assessment.id);
      }
      if (prev.length >= MAX_COMPARISON_ITEMS) {
        return prev; // Max reached
      }
      return [...prev, assessment];
    });
    // Any time selection changes, clear the previous "select min 2" error
    // so the UI doesn't show a stale message after the user adds more.
    setAnalysisError(null);
  }, []);

  const clearComparison = useCallback(() => {
    setSelectedForComparison([]);
    setComparisonDrillDown(null);
    setSelectedCategory(null);
    setComparisonAnalysis(null);
    setAnalysisError(null);
  }, []);

  const generateComparisonAnalysis = useCallback(async () => {
    const currentSelection = selectionRef.current;
    if (currentSelection.length < 2) {
      setAnalysisError('Selecteer minimaal 2 quickscans om te vergelijken');
      return;
    }

    setAnalysisLoading(true);
    setAnalysisError(null);

    try {
      const convertDimensionScores = (dimScores) => {
        if (!dimScores) return { people: 0, governance: 0, strategy: 0, technology: 0 };

        if (typeof dimScores === 'object' && !Array.isArray(dimScores)) {
          return {
            people: dimScores.people || dimScores['Mensen & Change'] || dimScores['Skills & Change'] || 0,
            governance: dimScores.governance || dimScores['Data & Governance'] || dimScores['Governance'] || 0,
            strategy: dimScores.strategy || dimScores['Strategie'] || dimScores['Adoption'] || 0,
            technology: dimScores.technology || dimScores['Technologie & Data'] || dimScores['Processes'] || 0,
          };
        }

        if (Array.isArray(dimScores)) {
          const result = { people: 0, governance: 0, strategy: 0, technology: 0 };
          dimScores.forEach((d) => {
            const dim = d.dimension?.toLowerCase() || '';
            const score = d.score || 0;
            if (dim.includes('skill') || dim.includes('change') || dim.includes('mensen')) {
              result.people = score;
            } else if (dim.includes('governance') || dim.includes('data')) {
              result.governance = score;
            } else if (dim.includes('adoption') || dim.includes('strateg')) {
              result.strategy = score;
            } else if (dim.includes('process') || dim.includes('technolog')) {
              result.technology = score;
            }
          });
          return result;
        }

        return { people: 0, governance: 0, strategy: 0, technology: 0 };
      };

      const assessmentData = currentSelection.map((a) => ({
        id: a.id,
        company: a.company,
        name: a.name,
        industry: a.industry,
        company_size: a.company_size,
        overall_score: a.overall_score,
        dimension_scores: convertDimensionScores(a.dimension_scores || a.scores),
        answers: a.answers || {},
      }));

      const analysis = await comparisonAPI.analyzeComparison(assessmentData);
      setComparisonAnalysis(analysis);
    } catch (error) {
      console.error('Failed to generate analysis:', error);
      let errorMessage = 'Er is een fout opgetreden bij het genereren van de analyse. Probeer het opnieuw.';

      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map((e) => e.msg || JSON.stringify(e)).join(', ');
        } else if (data.detail) {
          errorMessage = JSON.stringify(data.detail);
        } else if (data.message) {
          errorMessage = data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setAnalysisError(errorMessage);
    } finally {
      setAnalysisLoading(false);
    }
  }, []);

  const deleteSelectedAssessments = useCallback(async () => {
    const currentSelection = selectionRef.current;
    if (currentSelection.length === 0) return;

    const count = currentSelection.length;
    const confirmMessage = count === 1
      ? 'Weet u zeker dat u deze quickscan wilt verwijderen? Dit kan niet ongedaan worden gemaakt.'
      : `Weet u zeker dat u deze ${count} quickscans wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const deletePromises = currentSelection.map((a) => assessmentsAPI.delete(a.id));
      await Promise.all(deletePromises);

      const deletedIds = currentSelection.map((a) => a.id);
      onAssessmentsDeleted?.(deletedIds);

      clearComparison();

      alert(`${count} quickscan${count > 1 ? 's' : ''} succesvol verwijderd.`);
    } catch (error) {
      console.error('Failed to delete assessments:', error);
      alert('Er is iets misgegaan bij het verwijderen. Probeer het opnieuw.');
    }
  }, [onAssessmentsDeleted, clearComparison]);

  // Helpers for drill-down view
  const getAnswerValue = useCallback((assessment, questionId) => {
    if (!assessment.answers) return null;
    const answer = assessment.answers.find((a) => a.question_id === questionId);
    if (!answer) return null;
    if (answer.not_applicable) return 'N.v.t.';
    return answer.value;
  }, []);

  const getCategoryScore = useCallback((assessment, categoryId) => {
    if (!assessment.dimension_scores) return null;
    const dimMap = {
      strategy: ['Strategie', 'strategy', 'Adoption'],
      governance: ['Governance', 'governance', 'Processes'],
      technology: ['Technologie & Data', 'technology', 'Data & Governance'],
      people: ['Mensen & Change', 'people', 'Skills & Change'],
    };
    const possibleNames = dimMap[categoryId] || [];
    const dimScore = assessment.dimension_scores.find((ds) => possibleNames.includes(ds.dimension));
    return dimScore?.score || 0;
  }, []);

  // Radar chart data (memoized on selection)
  const radarData = useMemo(() => {
    if (selectedForComparison.length === 0) return [];

    const dimensions = ['Strategie', 'Governance', 'Technologie & Data', 'Mensen & Change'];
    const dimensionMap = {
      strategy: 'Strategie',
      governance: 'Governance',
      technology: 'Technologie & Data',
      people: 'Mensen & Change',
      Adoption: 'Strategie',
      Processes: 'Governance',
      'Data & Governance': 'Technologie & Data',
      'Skills & Change': 'Mensen & Change',
    };

    return dimensions.map((dim) => {
      const dataPoint = { dimension: dim };
      selectedForComparison.forEach((assessment, index) => {
        const dimScores = assessment.dimension_scores || [];
        const matchingScore = dimScores.find((ds) => {
          const normalizedDim = dimensionMap[ds.dimension] || ds.dimension;
          return normalizedDim === dim || ds.dimension === dim;
        });
        const rawScore = matchingScore?.score || 0;
        dataPoint[`score_${index}`] = Math.round((rawScore / 100) * 6 * 10) / 10;
      });

      if (selectedForComparison.length > 1) {
        const scores = selectedForComparison.map((_, idx) => dataPoint[`score_${idx}`] || 0);
        dataPoint.average = Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10;
      }

      return dataPoint;
    });
  }, [selectedForComparison]);

  // Aggregated stats (memoized)
  const comparisonStats = useMemo(() => {
    if (selectedForComparison.length === 0) return null;

    const scores = selectedForComparison.map((a) => a.overall_score);
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
    const min = Math.min(...scores);
    const max = Math.max(...scores);

    const industries = {};
    selectedForComparison.forEach((a) => {
      const ind = a.industry || 'Onbekend';
      industries[ind] = (industries[ind] || 0) + 1;
    });

    const sizes = {};
    selectedForComparison.forEach((a) => {
      const size = a.company_size || 'Onbekend';
      sizes[size] = (sizes[size] || 0) + 1;
    });

    return { avg: Math.round(avg), min, max, industries, sizes };
  }, [selectedForComparison]);

  return {
    // State
    selectedForComparison, setSelectedForComparison,
    comparisonDrillDown, setComparisonDrillDown,
    selectedCategory, setSelectedCategory,
    comparisonAnalysis,
    analysisLoading,
    analysisError,
    // Derived
    radarData, comparisonStats,
    // Actions / helpers
    toggleComparisonSelection,
    clearComparison,
    generateComparisonAnalysis,
    deleteSelectedAssessments,
    getAnswerValue,
    getCategoryScore,
    // Constants
    MAX_COMPARISON_ITEMS,
  };
};

export default useComparisonAdmin;
